/**
 * Onda Água v2 — THE KELVIN WAKE. Every wake in the world, in ONE draw call.
 *
 * A real ship's wake is not a stripe: it is a Kelvin pattern — two straight foam arms leaving the hull at
 * a fixed ±19.47° to the COURSE (a wave-dispersion constant: the angle does not change with speed, only
 * the brightness and how far back it survives), with a churned turbulent "stem" boiling straight down the
 * centreline behind the stern.
 *
 * PERFORMANCE LAW (the same one lights.ts follows). The old rig cost FOUR draw calls PER SHIP (ribbon +
 * bow/side foam billboards) — a busy AOI could spend 30+ draw calls on foam alone. This is ONE geometry,
 * ONE material, ONE mesh for every ship, the whale included: `MAX_WAKES` pre-allocated slots in a single
 * buffer, a compact slot allocator so the draw range stays tight, and `visible = false` (zero draw calls)
 * when nobody is moving. The bow foam it dropped came back for free in the OCEAN fragment shader, which
 * already knows every nearby hull's oriented ellipse.
 *
 * NO TEXTURES, NO CPU PARTICLES. The foam break-up is two octaves of hash noise generated in the fragment
 * shader from world XZ, so it is stable in the world (it does not crawl with the camera) and costs bytes,
 * not megabytes.
 *
 * GEOMETRY. Each slot is a trail of `WAKE_N` points laid at the stern every `WAKE_SPACING` metres of
 * travel, newest last. Each point carries NINE vertices — three 3-wide strips: left arm, stem, right arm —
 * so the arms stay narrow ribbons that hug the Gerstner sheet instead of one wide sheet that would cut
 * through crests. The lateral coordinate rides a vertex attribute and the fragment shades a smooth
 * `1 - lat²` profile across it, which is why 3 vertices per strip look like a soft band and not a tent.
 *
 * WORLD SPACE, ALWAYS. Points are stored where they were LAID, so the wake stays on the sea and bends
 * correctly through turns — a ship-locked quad with a painted V would swing the whole trail around on
 * every course change.
 *
 * QUALITY. `setQuality('low')` (Settings › Graphics › Low, a wallet webview, or `?water=low`) drops the
 * arms and shortens the stem: same single draw call, a fraction of the fill, and a captain on a potato
 * still sees his ship churn the water. Hot-swappable mid-battle — it is pure presentation.
 */
import * as THREE from 'three';
import { oceanSampleInto, type OceanSample } from '@piratas/sim';

/** Trail points per wake. × WAKE_SPACING = how far back the trail reaches. */
export const WAKE_N = 34;
/** Metres of travel between laid points. */
export const WAKE_SPACING = 2.4;
/** Seconds a laid point takes to dissolve completely (× top speed ≈ WAKE_N × WAKE_SPACING of trail). */
export const WAKE_LIFE = 6.5;
/** tan(19.47°) — the Kelvin half-angle. This is physics, not a taste knob: do not "open up the V". */
export const KELVIN_TAN = Math.tan((19.47 * Math.PI) / 180);
/** Hard cap on the arm offset (m) so a long straight run does not paint a 60 m white road. */
const ARM_MAX = 22;
/** Arm ribbon half-width at the stern, and how fast it softens with distance behind. */
const ARM_W0 = 0.55, ARM_GROW = 0.028;
/** Turbulent stem half-width at the stern (× half-beam) and its spread rate. */
const STEM_W0 = 0.75, STEM_GROW = 0.05;
/** How much of its full width the stem is BORN with at the transom, and the metres of travel it takes to
 *  open to full. A stem born at full beam is a straight-edged slab leaving the hull — the "faixa de estrada"
 *  the owner's QA reported. Real churn starts as a narrow boil in the propeller/rudder race and spreads. */
const STEM_BIRTH_W = 0.26, STEM_OPEN = 11;
/** Spread of the per-point life multiplier (× WAKE_LIFE). 0 = every point dies at the same age, i.e. the
 *  trail ends on a perfectly straight line across the sea; 0.42 = points die decorrelated and the tail
 *  frays into patches, which is what dissipating foam actually does. */
const LIFE_JITTER = 0.42;
/** Peak opacity of an arm / of the stem. Foam is a VEIL on the sea — past ~0.3 it reads as painted rope. */
const ARM_GAIN = 0.22, STEM_GAIN = 0.26;
/** Metres behind the stern where the stem has boiled away (the arms outlive it — as at sea). */
const STEM_LEN = 42;
/** Height (m) the foam floats above the DISPLACED wave sheet. Paired with a depth bias, this is what
 *  keeps the foam from z-fighting the sea without letting it float visibly off a crest. */
const WAKE_H = 0.17;
/** Concurrent wakes. Beyond this the furthest simply goes without — AOI rarely shows more at once. */
export const MAX_WAKES = 14;

const LANES = 9;                       // 3 strips × (outer, centre, inner)
const VERTS_PER_SLOT = WAKE_N * LANES;
const TRIS_PER_SLOT = (WAKE_N - 1) * 12;
const IDX_PER_SLOT = TRIS_PER_SLOT * 3;

/** One ship's (or the whale's) live trail. */
interface Trail {
  slot: number;
  /** flat [x, z, strength, birthT] per laid point, newest last (stride 4). */
  hist: number[];
  lastX: number; lastZ: number;
  /** half-beam (m) of the hull that owns it — sets the stem width and where the arms start. */
  beam: number;
  /** touched this frame? (untouched trails still age out, then free their slot) */
  live: boolean;
}

/** Public shape of `stats()` — what the render-proof reads back. */
export interface WakeStats { slots: number; triangles: number; drawCalls: number; quality: 'low' | 'high'; }

export class WakeSystem {
  readonly mesh: THREE.Mesh;
  private readonly mat: THREE.ShaderMaterial;
  private readonly geom: THREE.BufferGeometry;
  private readonly pos: Float32Array;
  private readonly foam: Float32Array;         // per-vertex alpha envelope
  private readonly trails = new Map<number, Trail>();
  private readonly freeSlots: number[] = [];
  private topSlot = 0;                          // slots [0, topSlot) may hold data -> the draw range
  private quality: 'low' | 'high' = 'high';
  private readonly _s0: OceanSample = { dx: 0, dy: 0, height: 0 };
  private readonly _s1: OceanSample = { dx: 0, dy: 0, height: 0 };
  private readonly _s2: OceanSample = { dx: 0, dy: 0, height: 0 };

  constructor(parent: THREE.Object3D) {
    const pos = new Float32Array(MAX_WAKES * VERTS_PER_SLOT * 3);
    const foam = new Float32Array(MAX_WAKES * VERTS_PER_SLOT);
    // lateral coordinate is STATIC: -1 / 0 / +1 across each of the three strips. The fragment shades
    // 1 - lat², so three vertices read as a soft band with no polygon edge and no texture.
    const lat = new Float32Array(MAX_WAKES * VERTS_PER_SLOT);
    // strip id: 0 = arm, 1 = stem. Lets the fragment give the churned centreline its own break-up.
    const kind = new Float32Array(MAX_WAKES * VERTS_PER_SLOT);
    const laneLat = [1, 0, -1, 1, 0, -1, 1, 0, -1];
    const laneKind = [0, 0, 0, 1, 1, 1, 0, 0, 0];
    for (let v = 0; v < lat.length; v++) { lat[v] = laneLat[v % LANES]!; kind[v] = laneKind[v % LANES]!; }

    const index = new Uint16Array(MAX_WAKES * IDX_PER_SLOT);
    let w = 0;
    for (let s = 0; s < MAX_WAKES; s++) {
      const sBase = s * VERTS_PER_SLOT;
      for (let i = 0; i < WAKE_N - 1; i++) {
        const a = sBase + i * LANES, b = sBase + (i + 1) * LANES;
        for (const strip of [0, 3, 6]) {
          for (const k of [0, 1]) {
            const p = strip + k;
            index[w++] = a + p;     index[w++] = a + p + 1; index[w++] = b + p;
            index[w++] = a + p + 1; index[w++] = b + p + 1; index[w++] = b + p;
          }
        }
      }
    }

    this.geom = new THREE.BufferGeometry();
    this.geom.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geom.setAttribute('aFoam', new THREE.BufferAttribute(foam, 1).setUsage(THREE.DynamicDrawUsage));
    this.geom.setAttribute('aLat', new THREE.BufferAttribute(lat, 1));
    this.geom.setAttribute('aKind', new THREE.BufferAttribute(kind, 1));
    this.geom.setIndex(new THREE.BufferAttribute(index, 1));
    this.geom.setDrawRange(0, 0);
    this.pos = pos; this.foam = foam;

    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0xe9f4ff) },
        uTime: { value: 0 },
        uNight: { value: 0 },   // 0 day … 1 deep night — foam is moonlit grey at midnight, never neon
        uStorm: { value: 0 },   // 0 calm … 1 full gale — see the storm block in the fragment
      },
      vertexShader: /* glsl */ `
        attribute float aFoam;
        attribute float aLat;
        attribute float aKind;
        varying float vA; varying float vLat; varying float vKind; varying vec2 vW; varying vec3 vWP;
        void main() {
          // the wake mesh carries ABSOLUTE world positions (it is parented at the origin, untransformed),
          // so the attribute doubles as the world point the fragment needs for its view + distance fades.
          vA = aFoam; vLat = aLat; vKind = aKind; vW = position.xz; vWP = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;   // the cell hash below must not be evaluated in fp16 (see wHash)
        uniform vec3 uColor; uniform float uTime; uniform float uNight; uniform float uStorm;
        varying float vA; varying float vLat; varying float vKind; varying vec2 vW; varying vec3 vWP;
        // precision-safe cell hash — see the note in storm.ts NOISE_GLSL (fp16 flattens fract(sin·BIG))
        float wHash(vec2 p){
          vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
          q += dot(q, q.yzx + 33.33);
          return fract((q.x + q.y) * q.z);
        }
        float wNoise(vec2 p){
          vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(wHash(i), wHash(i + vec2(1.0, 0.0)), f.x),
                     mix(wHash(i + vec2(0.0, 1.0)), wHash(i + vec2(1.0, 1.0)), f.x), f.y);
        }
        void main() {
          if (vA <= 0.004) discard;                       // the whole cost of an idle / Low-quality slot
          float d = vLat * vLat;
          float prof = max(0.0, 1.0 - d) * (1.0 - 0.45 * d); // soft band across the strip — no polygon edge
          // world-space break-up: the speckle sits on the SEA, so it does not crawl when the camera moves.
          // The stem boils (a slow drift term); the arms are steadier, which is what reads as "spray line".
          // NOTE the frequency: foam cells are metres wide, not centimetres. Fine noise on a band seen from
          // 40 m away averages to flat grey (and aliases when it does not) — big soft blobs are what reads.
          vec2 q = vW * 0.19 + vec2(uTime * 0.05 * vKind, uTime * -0.035 * vKind);
          float n = wNoise(q) * 0.50 + wNoise(q * 2.6 + 11.3) * 0.32 + wNoise(q * 6.1 + 3.7) * 0.18;
          // a SECOND, coarser field (~13 m) EATS THE EDGE: a perfect analytic band with a soft edge is
          // still a band (owner QA: "faixa de estrada"). Subtracting noise from the half-width makes both
          // edges a torn outline that wanders a metre or two. One lookup, on foam pixels only.
          float bite = wNoise(vW * 0.075 + vec2(uTime * 0.03, uTime * -0.02) + 27.3);
          prof *= smoothstep(0.0, 0.42, 1.0 - d - (0.62 - 0.72 * bite) * (0.35 + 0.65 * vKind));
          if (prof <= 0.0) discard;
          float churn = mix(0.34 + 1.35 * n, 0.02 + 2.15 * n, vKind);   // stem is patchier than the arms
          // stem octave CONTRAST: churn is white boil against holes of open sea, not an even grey
          churn *= mix(1.0, 0.55 + 1.05 * n * n * 2.0, vKind);
          // STORM. A gale does two things to a wake and this does both, for ~6 ALU and no draw call:
          //  (1) MORE foam — the sea is already aerated, so the same hull leaves a brighter trail;
          //  (2) a TORN trail — a second, coarser noise cut in as a mask RIPS holes in the band, so the
          //      wake stops being a continuous ribbon and becomes shredded patches the wind is pulling
          //      apart. Multiplying the existing churn (rather than adding a new term) is what keeps the
          //      calm look byte-identical when uStorm is 0.
          if (uStorm > 0.004) {
            float rip = wNoise(vW * 0.055 + vec2(uTime * 0.22, uTime * -0.14));
            churn *= mix(1.0, (1.55 + 0.55 * n) * smoothstep(0.16, 0.62, rip), uStorm);
          }
          // A flat foam band seen at a GRAZING angle foreshortens into a hard-edged bright sticker (a
          // straight-edged slab lying on the swell). Real foam is a thin scattering layer: the flatter you
          // look at it, the less of it you see. Fading it by the view elevation is what makes it read as
          // something ON the water instead of painted over it — and it also kills the silhouette.
          vec3 vd = cameraPosition - vWP;
          float dist = length(vd);
          float graze = smoothstep(0.020, 0.16, abs(vd.y) / max(dist, 1e-3));
          // and a long, soft distance fade so a remote captain's wake dissolves instead of popping at the cull
          float far = 1.0 - smoothstep(150.0, 275.0, dist);
          float a = clamp(vA * prof * churn * graze * far, 0.0, 1.0);
          if (a <= 0.006) discard;
          vec3 col = uColor * (1.0 - 0.55 * uNight);
          // 🦠 BIOLUMINESCENCE (see the file header). Rides the churn field that already exists, so the
          // glow lands where the water is broken; ADDED, not mixed, because plankton light is emission.
          // The threshold is HIGH and it rides prof: an evenly-lit stem came back as a neon slab.
          float bio = uNight * smoothstep(0.45, 1.7, churn) * prof * (0.30 + 0.70 * vKind);
          col += vec3(0.06, 0.92, 0.74) * bio * 0.34;
          gl_FragColor = vec4(pow(col, vec3(2.2)), min(1.0, a * (1.0 - 0.35 * uNight) + bio * 0.09));
        }
      `,
      transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
      // A hair of depth bias so the foam wins a TIE against the sea it lies on (hulls and islands still
      // occlude it normally) — but not enough to punch it through a crest it is genuinely behind, which
      // showed up in testing as straight-edged white slabs on the far side of a swell.
      polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -2,
    });

    this.mesh = new THREE.Mesh(this.geom, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1;
    this.mesh.visible = false;
    parent.add(this.mesh);
  }

  /** Low = stem only, half the trail, no arms (same one draw call, a fraction of the fill). */
  setQuality(q: 'low' | 'high'): void { this.quality = q === 'low' ? 'low' : 'high'; }
  get isLow(): boolean { return this.quality === 'low'; }

  /** Time, darkness + storm fury for the foam shading (called once per frame, before `flush`). */
  setEnv(t: number, night: number, storm = 0): void {
    this.mat.uniforms.uTime!.value = t;
    this.mat.uniforms.uNight!.value = night;
    this.mat.uniforms.uStorm!.value = storm;
  }

  /** Text-only render proof: slots in the draw range, their triangles, and the draw calls they cost. */
  stats(): WakeStats {
    const slots = this.topSlot;
    return { slots, triangles: slots * TRIS_PER_SLOT, drawCalls: this.mesh.visible ? 1 : 0, quality: this.quality };
  }

  /** Wipe a trail (respawn / teleport / sink) without giving up its slot. */
  reset(id: number): void { const tr = this.trails.get(id); if (tr) tr.hist.length = 0; }

  /**
   * Lay this frame's point for one wake owner. `x, z` is the STERN (where foam is actually born),
   * `strength` 0..1 the speed-derived brightness, `beam` the hull's half-beam in metres.
   * Returns false when no slot was free (the wake is simply skipped — never an error).
   */
  lay(id: number, x: number, z: number, strength: number, beam: number, t: number): boolean {
    let tr = this.trails.get(id);
    if (!tr) {
      const slot = this.freeSlots.pop() ?? (this.trails.size < MAX_WAKES ? this.trails.size : -1);
      if (slot < 0) return false;
      tr = { slot, hist: [], lastX: x, lastZ: z, beam, live: true };
      this.trails.set(id, tr);
    }
    tr.live = true; tr.beam = beam;
    if (Math.hypot(x - tr.lastX, z - tr.lastZ) > 30) tr.hist.length = 0; // teleport → start a fresh trail
    tr.lastX = x; tr.lastZ = z;
    const n = tr.hist.length / 4;
    const far = n === 0 || Math.hypot(x - tr.hist[(n - 1) * 4]!, z - tr.hist[(n - 1) * 4 + 1]!) > WAKE_SPACING;
    if (far && strength > 0.05) {
      tr.hist.push(x, z, Math.min(1, strength), t);
      while (tr.hist.length / 4 > WAKE_N) tr.hist.splice(0, 4);
    }
    return true;
  }

  /** Free an owner's slot (ship despawned / whale submerged / sailed past the cull radius). */
  drop(id: number): void {
    const tr = this.trails.get(id);
    if (!tr) return;
    this.blank(tr.slot);
    this.freeSlots.push(tr.slot);
    this.trails.delete(id);
  }

  /**
   * Rebuild every live trail into the shared buffers and upload ONCE. An owner that was not `lay`n this
   * frame (despawned, submerged, distance-culled) releases its slot, so the draw range only ever spans
   * wakes that are actually on screen.
   */
  flush(t: number, ampAt: (x: number, z: number) => number): void {
    const low = this.quality === 'low';
    let top = 0;
    // deleting the CURRENT key of a Map mid-iteration is well-defined in JS — so this stays allocation-free
    for (const [id, tr] of this.trails) {
      if (!tr.live) { this.drop(id); continue; }
      tr.live = false;
      // drop points that have fully dissolved — an idle ship's trail empties itself, then the slot is freed
      while (tr.hist.length >= 4 && t - tr.hist[3]! > WAKE_LIFE) tr.hist.splice(0, 4);
      if (tr.hist.length === 0) { this.drop(id); continue; }
      this.write(tr, t, ampAt, low);
      top = Math.max(top, tr.slot + 1);
    }
    this.topSlot = top;
    this.mesh.visible = top > 0;
    if (top === 0) { this.geom.setDrawRange(0, 0); return; }
    this.geom.setDrawRange(0, top * IDX_PER_SLOT);
    const p = this.geom.getAttribute('position') as THREE.BufferAttribute;
    const f = this.geom.getAttribute('aFoam') as THREE.BufferAttribute;
    p.clearUpdateRanges(); p.addUpdateRange(0, top * VERTS_PER_SLOT * 3); p.needsUpdate = true;
    f.clearUpdateRanges(); f.addUpdateRange(0, top * VERTS_PER_SLOT); f.needsUpdate = true;
  }

  /** Drop every trail and go dark (stepping ashore / a scene teardown). Idempotent; keeps the buffers. */
  clear(): void {
    for (const id of [...this.trails.keys()]) this.drop(id);
    this.topSlot = 0;
    this.geom.setDrawRange(0, 0);
    this.mesh.visible = false;
  }

  dispose(): void {
    this.mesh.parent?.remove(this.mesh);
    this.geom.dispose(); this.mat.dispose();
    this.trails.clear(); this.freeSlots.length = 0; this.topSlot = 0;
  }

  /* ------------------------------------------------------------------ */

  private blank(slot: number): void {
    this.foam.fill(0, slot * VERTS_PER_SLOT, (slot + 1) * VERTS_PER_SLOT);
  }

  /**
   * Write one trail's 9 × WAKE_N vertices: the Kelvin arms and the turbulent stem, riding the wave sheet.
   *
   * `ampAt` is the swell scale AT A POINT, not one global number: in a storm the shared coast envelope
   * shoals the sea near land, and a trail laid across a bay has to ride the water it was actually laid on
   * or the foam floats a metre above the calmed sheet by the beach. It is the SAME function the sea's
   * vertex shader and the hull bob call (Renderer.ampAt), so all three can never disagree.
   */
  private write(tr: Trail, t: number, ampAt: (x: number, z: number) => number, low: boolean): void {
    const h = tr.hist, n = h.length / 4;
    const base = tr.slot * VERTS_PER_SLOT;
    const stemLen = low ? STEM_LEN * 0.55 : STEM_LEN;
    for (let i = 0; i < WAKE_N; i++) {
      const vb = (base + i * LANES) * 3, ab = base + i * LANES;
      if (i >= n) { for (let k = 0; k < LANES; k++) this.foam[ab + k] = 0; continue; }
      const cx = h[i * 4]!, cz = h[i * 4 + 1]!, strength = h[i * 4 + 2]!, birth = h[i * 4 + 3]!;
      // path tangent from the neighbours (so the V bends through a turn instead of snapping)
      const pi = Math.max(0, i - 1), ni = Math.min(n - 1, i + 1);
      let dx = h[ni * 4]! - h[pi * 4]!, dz = h[ni * 4 + 1]! - h[pi * 4 + 1]!;
      const dl = Math.hypot(dx, dz) || 1; dx /= dl; dz /= dl;
      const px = -dz, pz = dx;                       // course normal
      // metres of PATH between this point and the stern — the Kelvin arm angle is measured against it,
      // which is exactly why the V opens at a constant 19.47° no matter how fast the ship is going.
      const behind = (n - 1 - i) * WAKE_SPACING;
      const age = t - birth;
      // EVERY POINT DIES ON ITS OWN CLOCK. One shared WAKE_LIFE meant the whole trail reached zero at the
      // same age — i.e. it ended on a straight line ruled across the sea, which is half of why it read as
      // a road (owner QA). A per-point life multiplier keyed on BIRTH (so it travels with the point and
      // never crawls) makes neighbours die tens of a second apart: the tail frays into patches instead of
      // being cut off. Free — it replaces one divide with one divide.
      // (kept at or BELOW 1 so `flush`'s WAKE_LIFE prune still outlives every point — a point pruned while
      // its own fade was still positive would pop out of existence, which is the one artefact worse than
      // a straight edge.)
      const lifeK = 1 - LIFE_JITTER * 0.5 * (1 + Math.sin(birth * 12.9 + 0.7));
      const fade = Math.max(0, 1 - age / (WAKE_LIFE * lifeK));
      const turb = 0.84 + 0.3 * Math.sin(birth * 7.3);          // keyed on BIRTH so it travels with the point
      const armOff = Math.min(ARM_MAX, tr.beam * 0.9 + KELVIN_TAN * behind);
      const armHalf = ARM_W0 + behind * ARM_GROW;
      // BORN NARROW, ON THE HULL. The churn leaves the transom as a tight boil and only spreads as it is
      // left behind; starting it at the full beam is what put a straight-edged slab against the stern.
      const stemHalf = tr.beam * STEM_W0 * Math.min(1, STEM_BIRTH_W + behind / STEM_OPEN) + behind * STEM_GROW;
      // …and the whole thing DISSIPATES NON-UNIFORMLY: a second, slower jitter (a different frequency, so
      // it does not beat with lifeK) thins some stretches of trail while leaving others thick.
      const patchy = 0.72 + 0.42 * Math.sin(birth * 3.1 + behind * 0.055);
      // arms: fade in over the first few metres (no hard start at the transom), then die with age
      const armA = low ? 0 : strength * fade * Math.min(1, behind / 5) * turb * patchy * ARM_GAIN;
      // stem: brightest right at the transom, boils away well before the arms do — and its tail is a
      // squared ramp rather than a linear one, so it thins out instead of stopping at a hard end.
      const stemTail = Math.max(0, 1 - behind / stemLen);
      const stemA = strength * Math.pow(fade, 1.5) * stemTail * stemTail * turb * patchy * STEM_GAIN * 1.25;
      // ONE Gerstner sample per strip (each strip is only a couple of metres wide, so the sheet is flat
      // across it) — three samples per point instead of nine, and the foam still hugs every crest.
      const alx = cx + px * armOff, alz = cz + pz * armOff;
      const arx = cx - px * armOff, arz = cz - pz * armOff;
      const sL = oceanSampleInto(this._s0, alx, alz, t, ampAt(alx, alz));
      const yL = sL.height + WAKE_H, lx = alx + sL.dx, lz = alz + sL.dy;
      const sC = oceanSampleInto(this._s1, cx, cz, t, ampAt(cx, cz));
      const yC = sC.height + WAKE_H, mx = cx + sC.dx, mz = cz + sC.dy;
      const sR = oceanSampleInto(this._s2, arx, arz, t, ampAt(arx, arz));
      const yR = sR.height + WAKE_H, rx = arx + sR.dx, rz = arz + sR.dy;
      // lanes 0-2 left arm, 3-5 stem, 6-8 right arm; lateral profile is the fragment's job
      const put = (lane: number, ox: number, oy: number, oz: number, a: number): void => {
        const j = vb + lane * 3;
        this.pos[j] = ox; this.pos[j + 1] = oy; this.pos[j + 2] = oz;
        this.foam[ab + lane] = a;
      };
      put(0, lx + px * armHalf, yL, lz + pz * armHalf, armA);
      put(1, lx, yL, lz, armA);
      put(2, lx - px * armHalf, yL, lz - pz * armHalf, armA);
      put(3, mx + px * stemHalf, yC, mz + pz * stemHalf, stemA);
      put(4, mx, yC, mz, stemA);
      put(5, mx - px * stemHalf, yC, mz - pz * stemHalf, stemA);
      put(6, rx + px * armHalf, yR, rz + pz * armHalf, armA);
      put(7, rx, yR, rz, armA);
      put(8, rx - px * armHalf, yR, rz - pz * armHalf, armA);
    }
  }
}
